import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import { useIntl } from "react-intl";

import "./WidgetDialog.scss";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import errorToast from "helpers/errorToast";
import { getWidgetLabelTranslations } from "helpers/translationHelpers";
import { updateSettings } from "api";
import testIds from "constants/testIds";
import WidgetTypes from "./components/WidgetTypes";
import GreetingsWidgetBuilder from "./components/GreetingsWidgetBuilder";
import StatusWidgetBuilder from "./components/StatusWidgetBuilder";
import MapWidgetBuilder from "./components/MapWidgetBuilder";
import ListWidgetBuilder from "./components/ListWidgetBuilder";
import QuickActionsWidgetBuilder from "./components/QuickActionsBuilder/QuickActionsWidgetBuilder";
import CalenderWidgetBuilder from "./components/CalendarWidgetBuilder/CalendarWidgetBuilder";

const WidgetDialog = function ({ isOpen, close, widgetId, dashboardWidgets, refreshToken }) {
  const intl = useIntl();

  const [widgetType, setWidgetType] = useState("");
  const [widgetContent, setWidgetContent] = useState("");
  const [widgetDataSource, setWidgetDataSource] = useState("");
  const [widgetFilters, setWidgetFilters] = useState({});

  useEffect(() => {
    clearState();
    if (widgetId >= 0 && isOpen) getWidget();
  }, [isOpen]);

  // Retrieve widget from local storage if widgetId prop is passed
  const getWidget = () => {
    const widgets = dashboardWidgets || [];
    const widget = widgets.find((widget) => widget.id === widgetId);
    setWidgetType(widget?.type);
    setWidgetContent(widget?.content);
    setWidgetDataSource(widget?.data_source);
    setWidgetFilters(widget?.filters);
  };

  const clearState = () => {
    setWidgetType("");
    setWidgetContent("");
    setWidgetDataSource("");
    setWidgetFilters({});
  };

  const handleWidgetContentClick = (widgetContent) => {
    setWidgetContent(widgetContent);
  };
  const handleWidgetTypeClick = (widgetType) => {
    setWidgetType(widgetType);
  };

  const handleWidgetDataSourceClick = (widgetDataSource) => {
    setWidgetContent("");
    setWidgetFilters({});
    setWidgetDataSource(widgetDataSource);
  };

  const addWidget = () => {
    if (widgetFilters?.payment_location === "all") delete widgetFilters.payment_location;
    const widgets = dashboardWidgets || [];
    const newId = widgets.length > 0 ? Math.max(...widgets.map((widget) => widget.id)) + 1 : 0;
    const newWidget = {
      id: newId,
      type: widgetType,
      data_source: widgetDataSource,
      content: widgetContent,
      filters: widgetFilters,
    };
    widgets.push(newWidget);
    localStorage.setItem("dashboard_widgets", JSON.stringify(widgets));
    close();
  };

  const deleteWidget = () => {
    const widgets = dashboardWidgets || [];
    const indexToDelete = widgets.findIndex((widget) => widget.id === widgetId);
    if (indexToDelete !== -1) {
      widgets.splice(indexToDelete, 1);
      localStorage.setItem("dashboard_widgets", JSON.stringify(widgets));
    }
    close();
  };

  const updateWidget = () => {
    const widgets = dashboardWidgets || [];
    const widgetIndex = widgets.findIndex((widget) => widget.id === widgetId);
    widgets[widgetIndex].data_source = widgetDataSource;
    widgets[widgetIndex].content = widgetContent;
    widgets[widgetIndex].filters = widgetFilters;
    updateSettings({ dashboard_widgets: widgets })
      .then(() => localStorage.setItem("dashboard_widgets", JSON.stringify(widgets)))
      .catch((err) => errorToast(err, "Could not update widget settings. Please try again later."));
    close();
  };

  const renderWidgetBuilder = () => {
    const baseProps = {
      handleWidgetDataSourceClick,
      handleWidgetContentClick,
      widgetContent: widgetContent || "",
      widgetDataSource: widgetDataSource || "",
      widgetFilters: widgetFilters || {},
      setWidgetFilters,
    };
    switch (widgetType) {
      case "Status":
        return <StatusWidgetBuilder refreshToken={refreshToken} {...baseProps} />;
      case "Greeting":
        return <GreetingsWidgetBuilder />;
      case "Map":
        return <MapWidgetBuilder {...baseProps} isEditMode={widgetId !== null} isOpen={isOpen} />;
      case "List":
        return <ListWidgetBuilder {...baseProps} />;
      case "QuickActions":
        return <QuickActionsWidgetBuilder {...baseProps} />;
      case "Calendar":
        return <CalenderWidgetBuilder {...baseProps} />;
      default:
        return null;
    }
  };

  const disableSaveChanges = (update) => {
    if (widgetType === "Calendar" && (widgetFilters?.address?.city || widgetFilters?.company)) return false;
    if (update && !widgetContent) return true;
    if (
      (!widgetContent && widgetType !== "Greeting" && widgetType !== "Map") ||
      (widgetType === "QuickActions" && widgetContent.length === 2)
    )
      return true;
    return false;
  };

  return (
    <Dialog
      className={`widget-dialog dashboard-container ${widgetType && "widget-dialog-bg-white"}`}
      show={isOpen}
      onHide={close}
      title={intl.formatMessage(
        {
          id: widgetId !== null ? "dashboard.widgetDialog.editWidget" : "dashboard.widgetDialog.addWidget",
          defaultMessage: widgetId !== null ? "Edit widget" : "Add widget",
        },
        {
          widgetType:
            widgetType === "QuickActions"
              ? getWidgetLabelTranslations("quick actions", intl).toLocaleLowerCase()
              : getWidgetLabelTranslations(widgetType?.toLowerCase(), intl).toLocaleLowerCase(),
        },
      )}
    >
      {/* Step 1 - Show widget type selection */}
      {!widgetType && <WidgetTypes handleWidgetTypeClick={handleWidgetTypeClick} dashboardWidgets={dashboardWidgets} />}

      {/* Step 2 - Render widget builder based on selected widget type */}
      {widgetType && renderWidgetBuilder()}

      {/* Dialog footer - renders different buttons based on if we're adding or editing a widget */}
      {widgetType && (
        <>
          <Row className="mb-3">
            <Col lg={12}>
              <div className="d-block w-100" />
            </Col>
          </Row>
          <Row style={{ marginTop: "30px" }}>
            <Col lg={12} className="d-flex flex-row">
              {(widgetId === 0 || widgetId) && (
                <>
                  <Button
                    variant="outline-primary"
                    style={{
                      borderRadius: "26px",
                      width: "82px",
                    }}
                    label={intl.formatMessage({
                      id: "general.cancel",
                      defaultMessage: "Cancel",
                    })}
                    onClick={close}
                  />
                  <Button
                    label={intl.formatMessage({
                      id: "general.delete",
                      defaultMessage: "Delete",
                    })}
                    style={{
                      borderRadius: "26px",
                      backgroundColor: "#EA432D",
                      padding: "0px 24px",
                      border: "none",
                    }}
                    className="ml-auto"
                    onClick={deleteWidget}
                    data-testid={testIds.WIDGET_DIALOG_DELETE_BUTTON}
                  />
                  <Button
                    className="ml-3"
                    style={{
                      borderRadius: "26px",
                      padding: "0px 24px",
                      backgroundColor: disableSaveChanges() ? "#DEE2E4" : "#0071CE",
                    }}
                    disabled={disableSaveChanges(true)}
                    label={intl.formatMessage({
                      id: "general.save",
                      defaultMessage: "Save",
                    })}
                    type="submit"
                    onClick={updateWidget}
                    data-testid={testIds.WIDGET_DIALOG_SAVE_BUTTON}
                  />
                </>
              )}
              {widgetId === null && (
                <>
                  <Button
                    variant="outline-primary"
                    style={{
                      borderRadius: "26px",
                      width: "73px",
                    }}
                    label={intl.formatMessage({
                      id: "general.back",
                      defaultMessage: "Back",
                    })}
                    onClick={() => {
                      clearState();
                    }}
                  />
                  <Button
                    className="ml-auto"
                    style={{
                      borderRadius: "26px",
                      backgroundColor: disableSaveChanges() ? "#DEE2E4" : "#0071CE",
                      width: "104px",
                      height: "32px",
                      border: "none",
                      color: disableSaveChanges() ? "#A6ABAD" : "#FFFFFF",
                    }}
                    disabled={disableSaveChanges()}
                    label={intl.formatMessage(
                      {
                        id: "dashboard.widgetDialog.addWidget",
                        defaultMessage: "Add widget",
                      },
                      {
                        widgetType: "",
                      },
                    )}
                    type="submit"
                    onClick={addWidget}
                    data-testid={testIds.WIDGET_DIALOG_ADD_BUTTON}
                  />
                </>
              )}
            </Col>
          </Row>
        </>
      )}
    </Dialog>
  );
};

WidgetDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  widgetId: PropTypes.number,
  dashboardWidgets: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  refreshToken: PropTypes.bool.isRequired,
};

WidgetDialog.defaultProps = {
  widgetId: null,
  dashboardWidgets: [],
};

export default WidgetDialog;
